<template>
    <v-card variant="outlined">
        <div class="d-flex wrapper">
            <v-img
                tile
                :src="$filters.imageUrl(mainImg, 400)"
                :alt="product.info.modelName"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                width="200"
                max-width="200"
                height="200"
                eager
                class="me-2"
            >
                <template v-slot:placeholder>
                    <v-row
                        v-show="!imgError && product.info.imageUrl"
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                    >
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <div class="ma-2 flex-grow-1 flex-shrink-1 d-flex flex-column justify-space-between text-truncate">
                <div>
                    <div class="mb-1 font-weight-medium text-truncate text-primary" :title="product.info.modelName">
                        {{ product.info.modelName }}
                        <ProductOfferTipsDialog
                            v-if="product.offer.tipsAvailable"
                            :offer-key="product.offer.offerKey"
                        />
                    </div>
                    <supplier-name-label v-if="isTO1" :supplier-name="product.info.supplierName" class="mb-1" />
                    <car-feature-labels :info="product.info" small class="mb-2" />
                    <car-characteristic :info="product.info" />
                    <div v-if="!hideMap" class="cursor-pointer" @click="mapModal = true">
                        <v-icon size="small" color="info">mdi-map-marker</v-icon>
                        <span class="text-caption text-primary text-decoration-underline text-lowercase">
                            {{ $t('show_on_map') }}
                        </span>
                    </div>
                    <div v-if="product.info.shortDescription" v-html="product.info.shortDescription" />
                </div>
                <div>
                    <div v-if="discountDetails" class="d-flex flex-shrink-0 width-max-content">
                        <special-offer-label :details="discountDetails" />
                    </div>
                    <div class="wrap-words">
                        <div v-if="product.offer.available" class="d-flex align-center text-caption">
                            <v-icon :title="$t('available')" start size="small" class="text-success">
                                mdi-checkbox-marked-circle
                            </v-icon>
                            <span class="text-success" v-text="$t('available')" />
                        </div>
                        <div v-else class="d-flex align-center text-caption">
                            <v-icon :title="$t('on_request')" start size="small" class="text-warning"
                                >mdi-alert-circle</v-icon
                            >
                            <span class="text-warning" v-text="$t('on_request')" />
                        </div>
                    </div>
                    <span v-if="product.offer && product.offer.price" class="text-caption mt-1">
                        <conditions-label
                            :offer-key="product.offer.offerKey"
                            :price="product.offer.price"
                            @conditions="showConditionsInfo"
                        />
                    </span>
                    <feature-includes class="mt-1" :features="product.info.features" />
                </div>
                <v-row v-if="$route.name === 'home'" dense>
                    <v-col cols="12">
                        <car-rent-points :offer="product.offer" class="mt-2" />
                    </v-col>
                </v-row>
            </div>
            <div v-if="!bookDisabled" class="ma-4 price-col flex-shrink-0 text-right">
                <discount-label :price="product.offer.price" class="d-inline-block" />
                <div data-cy="client-price" class="font-weight-medium">
                    {{ $filters.price(product.offer.price) }}
                </div>
                <commission-amount :commission="product.offer.price.commission" />
                <v-btn
                    v-if="!product.offer.soldOut"
                    variant="flat"
                    block
                    color="primary"
                    class="my-2"
                    @click="selectForBooking"
                >
                    {{ $t('book') }}
                </v-btn>
                <sold-out-modal v-else small />
                <div class="text-primary text-center cursor-pointer text-no-wrap" @click="showOfferInfo">
                    <v-icon size="small" color="primary">mdi-information-outline</v-icon>
                    <span class="show-description-label">
                        {{ `${$t('show')} ${$t('description').toLowerCase()}` }}
                    </span>
                </div>
            </div>
        </div>
        <client-only v-if="!hideMap">
            <v-dialog v-model="mapModal" :max-width="600">
                <car-map-points
                    v-model="mapExpanded"
                    :pick-up-points="product.offer.pickUpPoints"
                    :return-points="product.offer.returnPoints"
                    :parent="true"
                    style="max-height: 500px; position: unset"
                />
            </v-dialog>
        </client-only>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import DiscountLabel from '@/components/snippets/DiscountLabel.vue'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import ConditionsLabel from '~/components/snippets/ConditionsLabel.vue'
    import FeatureIncludes from '@/components/carsRent/snippets/FeatureIncludes.vue'
    import CarFeatureLabels from '@/components/carsRent/snippets/CarFeatureLabels.vue'
    import CarCharacteristic from '@/components/carsRent/snippets/CarCharacteristic.vue'
    import {EventBus, SHOW_CARS_CONDITIONS_INFO, SHOW_CARS_INFO_MODAL} from '@/utils/event-bus'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import CarMapPoints from '@/components/carsRent/snippets/CarMapPoints.vue'
    import CarRentPoints from '@/components/carsRent/snippets/CarRentPoints.vue'
    import SpecialOfferLabel from '~/components/hotels/search/offers/SpecialOfferLabel.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import SoldOutModal from '~src/components/snippets/SoldOutModal.vue'

    @Component({
        components: {
            SoldOutModal,
            ProductOfferTipsDialog,
            SpecialOfferLabel,
            CommissionAmount,
            ConditionsLabel,
            DiscountLabel,
            FeatureIncludes,
            CarFeatureLabels,
            CarCharacteristic,
            SupplierNameLabel,
            CarMapPoints,
            CarRentPoints,
        },
    })
    export class CarOfferCard extends Vue {
        @Prop({default: () => ({})}) product
        @Prop({default: false, type: Boolean}) bookDisabled
        @Prop({default: false, type: Boolean}) hideMap

        expanded = false
        imgError = false
        mapModal = false
        mapExpanded = false

        selectForBooking() {
            const offerKey = this.product.offer.offerKey
            carsRentStore.ADD_TO_BASKET({offerKey})
            this.$router.push({
                name: 'carBooking',
                query: {offerKey},
            })
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_CARS_CONDITIONS_INFO, {searchRequest: this.searchRequest, product: this.product})
        }

        showOfferInfo() {
            EventBus.$emit(SHOW_CARS_INFO_MODAL, this.product)
        }

        get mainImg() {
            return this.product.info.images?.find(item => item.mainImage)?.url || this.product.info.images?.length
                ? this.product.info.images[0]?.url
                : ''
        }

        get searchRequest() {
            return carsRentStore.searchRequest
        }

        get isTO1() {
            return authStore.isTO1
        }

        get discountDetails() {
            return this.product?.offer?.price?.discountDetails
        }
    }

    export default CarOfferCard
</script>

<style lang="scss" scoped>
    .price-col {
        width: 180px;
    }

    //TODO Remove after layout redesign
    @media (max-width: 1440px) {
        .price-col {
            width: 130px;
        }
    }
</style>
